<script>
    import { onMount } from "svelte";
    import {
        filteredSupplyPlan,
        appliedSupplyPlanSearch,
        ALL_WEEKS,
        MASTER_COLUMNS_DEF,
        METRIC_DEFINITIONS,
        isDataLoading,
        supplyPlanSort,
        DEFAULT_SORT,
    } from "./store.js";
    import { push } from "svelte-spa-router";
    import toast from "svelte-french-toast";
    import OrderDetailModal from "./OrderDetailModal.svelte";

    // ──────────────────────────────────────────────────────────────
    // 수주 상세 팝업 (수주 행 수량 더블클릭 → USP_MNGMT_DTL)
    // ──────────────────────────────────────────────────────────────
    // 표시 컬럼 → 프로시저 파라미터 매핑 (표시 중인 컬럼만 더블클릭한 행의 값을 보내고, 숨겨진 컬럼은 '' = 전체)
    const DETAIL_COL_PARAMS = {
        productType: { param: "mlsfc_code", field: "productTypeCode" },
        itemGroup: { param: "item_group_code", field: "itemGroupCode" },
        integratedModel: { param: "mhrls_unity_code", field: "integratedModelCode" },
        model: { param: "mhrls_knd_code", field: "modelCode" },
        itemCode: { param: "item_code", field: "itemCode" },
        itemName: { param: "item_nm", field: "itemName" },
    };

    let isDetailOpen = false;
    let detailParams = null;
    let detailMeta = { planWeek: "", periodLabel: "", filters: [] };
    // 팝업이 보여주고 있는 셀 (리스트에서 불 켜서 표시)
    let detailCellKey = "";

    // 셀 식별 키: 품목 행 + 편성주차 + 구분(W/M) + 열
    function orderCellKey(product, planWeek, periodSe, unit) {
        return `${product.id}|${planWeek}|${periodSe}|${unit.key}`;
    }

    // 더블클릭/더블탭 직접 감지: 터치 기기(태블릿)는 확대 가능한 viewport에서 dblclick 이벤트가 오지 않으므로
    // 같은 셀을 450ms 이내에 두 번 클릭(탭)하면 팝업을 연다. (마우스/터치 공통)
    const DOUBLE_TAP_MS = 450;
    let lastTap = { key: "", time: 0 };

    function handleOrderCellTap(product, planWeek, periodSe, unit, qty) {
        if (!qty || !unit) return;
        const key = orderCellKey(product, planWeek, periodSe, unit);
        const now = Date.now();
        if (lastTap.key === key && now - lastTap.time < DOUBLE_TAP_MS) {
            lastTap = { key: "", time: 0 };
            openOrderDetail(product, planWeek, periodSe, unit, qty);
        } else {
            lastTap = { key, time: now };
        }
    }

    // unit: 더블클릭한 열 객체 (주차 { year, week, label } / 월 { year, month, label })
    function openOrderDetail(product, planWeek, periodSe, unit, qty) {
        // 수량이 0인 셀은 호출하지 않음
        if (!qty || !unit) return;
        if (!planWeek || planWeek === "전체") {
            toast.error("편성주차를 선택한 뒤 조회해야 수주 상세를 볼 수 있습니다.", { duration: 2500 });
            return;
        }
        // 더블클릭으로 선택된 텍스트 선택 해제
        try { window.getSelection()?.removeAllRanges(); } catch (e) { /* 무시 */ }

        const isWeek = periodSe === "W";
        const year = String(unit.year || $filteredSupplyPlan.startYear || new Date().getFullYear());
        const period = String(isWeek ? unit.week : unit.month).padStart(2, "0");

        const p = {
            plan_week_no: String(planWeek).trim(),
            period_se: periodSe,
            year,
            period,
            mlsfc_code: "",
            item_group_code: "",
            mhrls_unity_code: "",
            mhrls_knd_code: "",
            item_code: "",
            item_nm: "",
        };
        const filters = [];
        for (const colKey of $filteredSupplyPlan.visibleCols) {
            const map = DETAIL_COL_PARAMS[colKey];
            if (!map) continue;
            const raw = String(product[map.field] ?? "").trim();
            p[map.param] = raw === "-" ? "" : raw;
            const def = MASTER_COLUMNS_DEF.find(c => c.key === colKey);
            const shown = String(product[colKey] ?? "").trim();
            if (def && shown && shown !== "-") filters.push({ label: def.label, value: shown });
        }

        detailParams = p;
        detailCellKey = orderCellKey(product, planWeek, periodSe, unit);
        detailMeta = {
            planWeek: p.plan_week_no,
            periodLabel: isWeek ? `${unit.label} 주차` : `${unit.label} 합계`,
            filters,
        };
        isDetailOpen = true;
    }

    // 월 합계 모드: 주차/총계 컬럼을 숨기고 월(또는 연도) 합계(노란색 칸)만 표시: 주차/총계 컬럼을 숨기고 월(또는 연도) 합계(노란색 칸)만 표시
    $: monthSummaryMode = Boolean($filteredSupplyPlan.monthSummaryMode);
    // 전체 합계 행에 표시할 항목 (표시 항목 설정 + 0 제외 반영)
    $: grandDefs = $filteredSupplyPlan.grandMetricDefs || METRIC_DEFINITIONS;

    // 컬럼 너비 상태 (기본값: 최소 80px 이상, 글자가 잘리지 않도록 여유 있게 설정)
    let colWidths = {
        productType: 95,
        itemGroup: 130,
        integratedModel: 85,
        model: 85,
        itemCode: 110,
        itemName: 175,
    };

    // 텍스트 시각적 너비 추정 (한글 13px, 영문 대문자 9px, 영숫자/기호 7.5px)
    function estimateTextWidth(text) {
        if (!text) return 0;
        let width = 0;
        const str = String(text);
        for (let i = 0; i < str.length; i++) {
            const code = str.charCodeAt(i);
            if (code > 127) {
                width += 13;
            } else if (code >= 65 && code <= 90) {
                width += 9;
            } else {
                width += 7.5;
            }
        }
        return width;
    }

    // 해당 컬럼의 실제 데이터 중 가장 긴 텍스트 기준 최적 너비 계산
    function calculateMaxContentWidth(colKey, items) {
        const def = MASTER_COLUMNS_DEF.find(c => c.key === colKey);
        let maxVisual = def ? estimateTextWidth(def.label) : 40;

        if (Array.isArray(items) && items.length > 0) {
            // 상위 100개 항목을 순회하며 가장 긴 텍스트 탐색
            const limit = Math.min(items.length, 100);
            for (let i = 0; i < limit; i++) {
                const val = items[i]?.product?.[colKey];
                if (val) {
                    const w = estimateTextWidth(val);
                    if (w > maxVisual) maxVisual = w;
                }
            }
        }
        // 셀 내부 padding (좌우 14px) + 테두리 + 여유공간 (18px)
        const computed = Math.ceil(maxVisual + 32);
        // 최소 80px 보장, 최대 500px 제한
        return Math.max(80, Math.min(computed, 500));
    }

    // 마우스 드래그를 통한 컬럼 너비 조절 (Column Resizing)
    let activeResizeCol = null;
    let resizeStartX = 0;
    let resizeStartWidth = 0;
    let resizeMaxWidth = 500;

    function handleResizeStart(colKey, e) {
        activeResizeCol = colKey;
        resizeStartX = e.clientX;
        resizeStartWidth = colWidths[colKey] || 80;

        // 최대 허용 폭: 데이터 내 가장 긴 글자 너비 (단 최소 80px 이상)
        const maxContentW = calculateMaxContentWidth(colKey, $filteredSupplyPlan.items);
        resizeMaxWidth = Math.max(100, maxContentW);

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        window.addEventListener("mousemove", handleResizeMouseMove);
        window.addEventListener("mouseup", handleResizeMouseUp);
    }

    function handleResizeMouseMove(e) {
        if (!activeResizeCol) return;
        const delta = e.clientX - resizeStartX;
        // 최소 80px, 최대: 가장 긴 글자 너비(resizeMaxWidth)
        const newWidth = Math.max(80, Math.min(resizeMaxWidth, resizeStartWidth + delta));
        colWidths = {
            ...colWidths,
            [activeResizeCol]: newWidth,
        };
    }

    function handleResizeMouseUp() {
        lastResizeEnd = Date.now();
        activeResizeCol = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", handleResizeMouseMove);
        window.removeEventListener("mouseup", handleResizeMouseUp);
    }

    // 컬럼 너비 조절 직후 발생하는 click이 정렬로 이어지지 않도록 종료 시각을 기억
    let lastResizeEnd = 0;

    // 활성 정렬 컬럼 (선택된 컬럼이 숨겨져 있으면 기본 정렬 컬럼으로 표시)
    $: activeSortKey = $filteredSupplyPlan.visibleCols.includes($supplyPlanSort.key)
        ? $supplyPlanSort.key
        : DEFAULT_SORT.key;
    $: activeSortDir = activeSortKey === $supplyPlanSort.key ? $supplyPlanSort.dir : DEFAULT_SORT.dir;

    // 헤더 클릭: 다른 컬럼이면 오름차순부터, 같은 컬럼이면 오름 ↔ 내림 전환
    function handleSortClick(colKey) {
        if (Date.now() - lastResizeEnd < 300) return;
        supplyPlanSort.set({
            key: colKey,
            dir: activeSortKey === colKey && activeSortDir === "asc" ? "desc" : "asc",
        });
    }

    // 더블클릭 시 가장 긴 글자 너비로 자동 맞춤 (Auto-Fit)
    function handleAutoFit(colKey) {
        const autoWidth = calculateMaxContentWidth(colKey, $filteredSupplyPlan.items);
        colWidths = {
            ...colWidths,
            [colKey]: autoWidth,
        };
    }

    function formatWeekLabel(w) {
        if (typeof w === "object" && w.label) return w.label;
        const startY = $appliedSupplyPlanSearch?.startYear || new Date().getFullYear();
        const year2 = String(startY).slice(-2);
        return `${year2}-${String(w).padStart(2, "0")}`;
    }

    function getColLeft(visibleCols, targetKey, widths) {
        let left = 0;
        for (const key of visibleCols) {
            if (!MASTER_COLUMNS_DEF.some(c => c.key === key)) continue;
            if (key === targetKey) return `${left}px`;
            left += (widths[key] || 80);
        }
        return `${left}px`;
    }

    function getPlanWeekLeft(visibleCols, widths) {
        let left = 0;
        for (const key of visibleCols) {
            if (!MASTER_COLUMNS_DEF.some(c => c.key === key)) continue;
            left += (widths[key] || 80);
        }
        return `${left}px`;
    }

    function getMetricLeft(visibleCols, widths) {
        let left = 0;
        for (const key of visibleCols) {
            if (!MASTER_COLUMNS_DEF.some(c => c.key === key)) continue;
            left += (widths[key] || 80);
        }
        return `${left + 85}px`;
    }

    function formatNumber(num) {
        if (num === undefined || num === null) return "0";
        return Number(num).toLocaleString();
    }

    function formatDiff(num) {
        if (num === undefined || num === null || num === 0) return "-";
        if (num > 0) return `+${num}`;
        return `${num}`;
    }

    function getWeekVal(metrics, week, key) {
        if (!metrics || !metrics.weeksData) return 0;
        const wKey = typeof week === "object" ? week.key : week;
        return metrics.weeksData[wKey]?.[key] ?? 0;
    }

    function getMonthVal(metrics, month, key) {
        if (!metrics || !metrics.monthsData) return 0;
        const mKey = typeof month === "object" ? (month.key || month.month) : month;
        return metrics.monthsData[mKey]?.[key] ?? 0;
    }

    function getTotalVal(metrics, key) {
        if (!metrics || !metrics.totalData) return 0;
        return metrics.totalData[key] ?? 0;
    }

    function isActualWeek(week) {
        if (typeof week === "object" && week.isActual !== undefined) return Boolean(week.isActual);
        const wObj = ALL_WEEKS.find(x => x.week === week);
        return wObj ? wObj.isActual : false;
    }

    // 무한 스크롤(점진적 렌더링) 설정
    const INITIAL_COUNT = 30;
    const BATCH_SIZE = 30;
    let displayCount = INITIAL_COUNT;
    let scrollWrapper;

    let prevItemsRef = null;
    $: if ($filteredSupplyPlan.items && $filteredSupplyPlan.items !== prevItemsRef) {
        prevItemsRef = $filteredSupplyPlan.items;
        displayCount = INITIAL_COUNT;
        if (scrollWrapper) {
            scrollWrapper.scrollTop = 0;
        }
    }

    $: displayedItems = ($filteredSupplyPlan.items || []).slice(0, displayCount);
    $: hasMore = ($filteredSupplyPlan.items || []).length > displayCount;

    let isScrolling = false;
    function handleScroll() {
        if (!hasMore || isScrolling) return;
        isScrolling = true;
        window.requestAnimationFrame(() => {
            if (scrollWrapper) {
                const scrollBottom = scrollWrapper.scrollHeight - scrollWrapper.scrollTop - scrollWrapper.clientHeight;
                if (scrollBottom < 600) {
                    displayCount = Math.min(displayCount + BATCH_SIZE, $filteredSupplyPlan.items.length);
                }
            }
            isScrolling = false;
        });
    }

    function loadAll() {
        displayCount = $filteredSupplyPlan.items.length;
    }

    // 전체 합계 상단 표시 토글 (백그라운드 사전 렌더링 + 0ms 즉각 반응 스르륵 슬라이드)
    let isSummaryOpen = false;
    function toggleTopSummary() {
        isSummaryOpen = !isSummaryOpen;
    }

    // Ctrl 키 누른 상태에서 마우스 드래그로 화면 이동 (Pan / Grab Tool)
    let isCtrlDown = false;
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;

    // 드래그로 선택된 텍스트가 있으면 패닝 모드(user-select: none)를 켜지 않음 → Ctrl+C 복사 가능
    function hasTextSelection() {
        const sel = window.getSelection();
        return !!sel && !sel.isCollapsed && sel.toString().length > 0;
    }

    function handleKeyDown(e) {
        if (e.key === "Control" || e.ctrlKey || e.key === "Meta" || e.metaKey) {
            if (hasTextSelection()) return;
            isCtrlDown = true;
        }
    }

    function handleKeyUp(e) {
        if (e.key === "Control" || e.key === "Meta" || (!e.ctrlKey && !e.metaKey)) {
            isCtrlDown = false;
            if (isPanning) {
                isPanning = false;
            }
        }
    }

    function handleWindowBlur() {
        isCtrlDown = false;
        isPanning = false;
    }

    function handleMouseDown(e) {
        if ((e.ctrlKey || e.metaKey || isCtrlDown) && e.button === 0) {
            if (!scrollWrapper) return;
            isPanning = true;
            startX = e.clientX;
            startY = e.clientY;
            startScrollLeft = scrollWrapper.scrollLeft;
            startScrollTop = scrollWrapper.scrollTop;
            e.preventDefault();
        }
    }

    function handleWindowMouseMove(e) {
        if (!isPanning) {
            isCtrlDown = !!(e.ctrlKey || e.metaKey) && !hasTextSelection();
            return;
        }
        if (!scrollWrapper) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        scrollWrapper.scrollLeft = startScrollLeft - dx;
        scrollWrapper.scrollTop = startScrollTop - dy;
    }

    function handleWindowMouseUp() {
        if (isPanning) {
            isPanning = false;
        }
    }

    onMount(() => {
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);
        window.addEventListener("blur", handleWindowBlur);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("mouseup", handleWindowMouseUp);
            window.removeEventListener("blur", handleWindowBlur);
        };
    });
</script>

<div class="list-container">
    <!-- 로딩 오버레이 (조회 중 전체 페이지 표시) -->
    {#if $isDataLoading}
        <div class="loading-overlay">
            <div class="loading-modal">
                <div class="spinner"></div>
                <div class="loading-text">
                    <strong>공급계획(PSI) 데이터 조회 및 계산 중...</strong>
                    <span>주차별 수주, 생산, 출하, 재고 집계를 동기화하고 있습니다.</span>
                </div>
            </div>
        </div>
    {/if}

    <div class="list-header">
        <div class="title-area">
            <div class="title-left">
                <h3>
                    📊 공급계획(PSI) 주차별 집계표
                    {#if $filteredSupplyPlan.visibleCols.includes("itemCode")}
                        <span class="badge-count">총 {$filteredSupplyPlan.items.length}개 품번</span>
                    {:else}
                        <span class="badge-count">총 {$filteredSupplyPlan.items.length}개 항목 (총 {$filteredSupplyPlan.totalItemCount}개 품번)</span>
                    {/if}
                </h3>
            </div>
            <div class="header-right-group">
                <div class="header-action-group">
                    <button
                        type="button"
                        class="action-btn toggle-summary-btn"
                        class:is-active={isSummaryOpen}
                        on:click={toggleTopSummary}
                        title={isSummaryOpen ? '상단 전체 합계 행 접기' : '상단 전체 합계 행 펼쳐보기'}
                    >
                        {#if isSummaryOpen}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
                                <polyline points="18 15 12 9 6 15"/>
                            </svg>
                            <span>📊 전체합계 숨기기</span>
                        {:else}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                            <span>📊 전체합계 상단 보기</span>
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div
        class="table-scroll-wrapper"
        class:pan-ready={isCtrlDown && !isPanning}
        class:is-panning={isPanning}
        bind:this={scrollWrapper}
        on:scroll={handleScroll}
        on:mousedown={handleMouseDown}
        on:dragstart|preventDefault
    >
        <table class="psi-table" class:month-sum-mode={monthSummaryMode}>
            <thead>
                <tr class="psi-header-row">
                    {#each $filteredSupplyPlan.visibleCols as colKey}
                        {@const def = MASTER_COLUMNS_DEF.find(c => c.key === colKey)}
                        {#if def}
                            <th
                                class="sticky-col col-custom sortable-th"
                                class:sort-active={activeSortKey === colKey}
                                style="left: {getColLeft($filteredSupplyPlan.visibleCols, colKey, colWidths)}; width: {colWidths[colKey] || 80}px; min-width: {colWidths[colKey] || 80}px; max-width: {colWidths[colKey] || 80}px;"
                                aria-sort={activeSortKey === colKey ? (activeSortDir === "asc" ? "ascending" : "descending") : "none"}
                                on:click={() => handleSortClick(colKey)}
                                title="클릭: {def.label} 기준 정렬 (오름차순 ↔ 내림차순)"
                            >
                                <span class="col-header-text">
                                    {def.label}
                                    <span class="sort-icon" aria-hidden="true">{activeSortKey === colKey ? (activeSortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
                                </span>
                                <div
                                    class="col-resize-handle"
                                    class:is-active={activeResizeCol === colKey}
                                    on:mousedown|stopPropagation={(e) => handleResizeStart(colKey, e)}
                                    on:click|stopPropagation
                                    on:dblclick|stopPropagation={() => handleAutoFit(colKey)}
                                    title="드래그: 너비 조절 (최소 80px) / 더블클릭: 가장 긴 글자에 맞춤"
                                ></div>
                            </th>
                        {/if}
                    {/each}
                    <th
                        class="sticky-col col-planweek"
                        style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                    >
                        계획주차
                    </th>
                    <th
                        class="sticky-col col-metric"
                        style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                    >
                        항목
                    </th>

                    {#each $filteredSupplyPlan.activeMonths as m}
                        {#each (monthSummaryMode ? [] : m.weeks) as w}
                            <th class="week-th" class:actual-th={isActualWeek(w)}>
                                <span>{typeof w === 'object' ? w.label : formatWeekLabel(w)}</span>
                            </th>
                        {/each}
                        <th class="month-subtotal-th">
                            {m.label}
                        </th>
                    {/each}
                    <th class="total-th">계 (Total)</th>
                </tr>

                <!-- [상단 전체합계 행] 백그라운드에 사전 렌더링하여 클릭 시 0ms 렉 없이 즉각 스르륵 개폐 -->
                {#if $filteredSupplyPlan.items.length > 0}
                    {@const grandBase = $filteredSupplyPlan.grandBase}
                    {@const grandCompare = $filteredSupplyPlan.grandCompare}
                    {@const grandDiff = $filteredSupplyPlan.grandDiff}
                    {@const totalMasterCount = $filteredSupplyPlan.visibleCols.filter(k => MASTER_COLUMNS_DEF.some(c => c.key === k)).length}

                    {#each grandDefs as metric, mIdx}
                        <tr
                            class="grand-summary-row summary-top-mode"
                            class:summary-first={mIdx === 0}
                            class:summary-collapsed={!isSummaryOpen}
                        >
                            {#if mIdx === 0}
                                <td
                                    colspan={totalMasterCount}
                                    rowspan={$filteredSupplyPlan.isMultiWeek ? grandDefs.length * 3 : grandDefs.length}
                                    class="sticky-col col-total-label bold"
                                    style="left: 0;"
                                >
                                    <div class="summary-cell-wrap summary-total-badge" class:is-open={isSummaryOpen}>
                                        전체 합계 (Total)
                                    </div>
                                </td>
                                <td
                                    rowspan={grandDefs.length}
                                    class="sticky-col col-planweek planweek-cell font-mono bold"
                                    style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                >
                                    <div class="summary-cell-wrap summary-planweek-wrap" class:is-open={isSummaryOpen}>
                                        {$filteredSupplyPlan.baseWeek}
                                    </div>
                                </td>
                            {/if}

                            <td
                                class="sticky-col col-metric metric-cell bold"
                                class:metric-shortage={metric.key === 'shortage'}
                                style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                            >
                                <div class="summary-cell-wrap" class:is-open={isSummaryOpen}>
                                    {metric.label}
                                </div>
                            </td>

                            {#each $filteredSupplyPlan.activeMonths as m}
                                {#each (monthSummaryMode ? [] : m.weeks) as w}
                                    <td class="cell-num bold" class:actual-cell={isActualWeek(w)}>
                                        <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                            {formatNumber(getWeekVal(grandBase, w, metric.key))}
                                        </div>
                                    </td>
                                {/each}
                                <td class="cell-num month-subtotal-cell bold">
                                    <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                        {formatNumber(getMonthVal(grandBase, m, metric.key))}
                                    </div>
                                </td>
                            {/each}

                            <td class="cell-num grand-total-cell bold text-primary">
                                <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                    {formatNumber(getTotalVal(grandBase, metric.key))}
                                </div>
                            </td>
                        </tr>
                    {/each}

                    {#if $filteredSupplyPlan.isMultiWeek}
                        {#each grandDefs as metric, mIdx}
                            <tr
                                class="grand-summary-row compare-row summary-top-mode"
                                class:summary-collapsed={!isSummaryOpen}
                            >
                                {#if mIdx === 0}
                                    <td
                                        rowspan={grandDefs.length}
                                        class="sticky-col col-planweek planweek-cell compare-week-tag font-mono bold"
                                        style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                    >
                                        <div class="summary-cell-wrap summary-planweek-wrap" class:is-open={isSummaryOpen}>
                                            {$filteredSupplyPlan.compareWeek}
                                        </div>
                                    </td>
                                {/if}
                                <td
                                    class="sticky-col col-metric metric-cell bold"
                                    class:metric-shortage={metric.key === 'shortage'}
                                    style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                                >
                                    <div class="summary-cell-wrap" class:is-open={isSummaryOpen}>
                                        {metric.label}
                                    </div>
                                </td>
                                {#each $filteredSupplyPlan.activeMonths as m}
                                    {#each (monthSummaryMode ? [] : m.weeks) as w}
                                        <td class="cell-num bold" class:actual-cell={isActualWeek(w)}>
                                            <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                                {formatNumber(getWeekVal(grandCompare, w, metric.key))}
                                            </div>
                                        </td>
                                    {/each}
                                    <td class="cell-num month-subtotal-cell bold">
                                        <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                            {formatNumber(getMonthVal(grandCompare, m, metric.key))}
                                        </div>
                                    </td>
                                {/each}
                                <td class="cell-num grand-total-cell bold">
                                    <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                        {formatNumber(getTotalVal(grandCompare, metric.key))}
                                    </div>
                                </td>
                            </tr>
                        {/each}

                        {#each grandDefs as metric, mIdx}
                            <tr
                                class="grand-summary-row diff-row summary-top-mode"
                                class:product-last-row={mIdx === grandDefs.length - 1}
                                class:summary-collapsed={!isSummaryOpen}
                            >
                                {#if mIdx === 0}
                                    <td
                                        rowspan={grandDefs.length}
                                        class="sticky-col col-planweek planweek-cell diff-tag font-mono bold"
                                        style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                    >
                                        <div class="summary-cell-wrap summary-planweek-wrap" class:is-open={isSummaryOpen}>
                                            차이 (Diff)
                                        </div>
                                    </td>
                                {/if}
                                <td
                                    class="sticky-col col-metric metric-cell diff-metric bold"
                                    style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                                >
                                    <div class="summary-cell-wrap" class:is-open={isSummaryOpen}>
                                        {metric.label}
                                    </div>
                                </td>
                                {#each $filteredSupplyPlan.activeMonths as m}
                                    {#each (monthSummaryMode ? [] : m.weeks) as w}
                                        <td
                                            class="cell-num diff-cell bold"
                                            class:diff-pos={getWeekVal(grandDiff, w, metric.key) > 0}
                                            class:diff-neg={getWeekVal(grandDiff, w, metric.key) < 0}
                                            class:actual-cell={isActualWeek(w)}
                                        >
                                            <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                                {formatDiff(getWeekVal(grandDiff, w, metric.key))}
                                            </div>
                                        </td>
                                    {/each}
                                    <td
                                        class="cell-num month-subtotal-cell diff-cell bold"
                                        class:diff-pos={getMonthVal(grandDiff, m, metric.key) > 0}
                                        class:diff-neg={getMonthVal(grandDiff, m, metric.key) < 0}
                                    >
                                        <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                            {formatDiff(getMonthVal(grandDiff, m, metric.key))}
                                        </div>
                                    </td>
                                {/each}
                                <td
                                    class="cell-num grand-total-cell diff-cell bold"
                                    class:diff-pos={getTotalVal(grandDiff, metric.key) > 0}
                                    class:diff-neg={getTotalVal(grandDiff, metric.key) < 0}
                                >
                                    <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                        {formatDiff(getTotalVal(grandDiff, metric.key))}
                                    </div>
                                </td>
                            </tr>
                        {/each}
                    {/if}
                {/if}
            </thead>

            <tbody>
                {#if $filteredSupplyPlan.items.length === 0}
                    <tr>
                        <td colspan="50" class="empty-state">
                            <div class="empty-content">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <circle cx="11" cy="11" r="8"/>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                                <p>조회 조건에 맞는 공급계획(PSI) 데이터가 없습니다.</p>
                            </div>
                        </td>
                    </tr>
                {:else}
                    {#each displayedItems as item}
                        {@const product = item.product}
                        {@const baseMetrics = item.baseMetrics}
                        {@const compareMetrics = item.compareMetrics}
                        {@const diffMetrics = item.diffMetrics}
                        {@const rowDefs = item.metricDefs || METRIC_DEFINITIONS}
                        {@const totalRowsPerProduct = $filteredSupplyPlan.isMultiWeek ? rowDefs.length * 3 : rowDefs.length}

                        <!-- 1. 기준 주차 (6행) -->
                        {#each rowDefs as metric, mIdx}
                            <tr class="data-row base-row" class:product-first-row={mIdx === 0}>
                                <!-- 제품 마스터 컬럼들 (첫 번째 행에서 rowspan 렌더링) -->
                                {#if mIdx === 0}
                                    {#each $filteredSupplyPlan.visibleCols as colKey}
                                        {@const def = MASTER_COLUMNS_DEF.find(c => c.key === colKey)}
                                        {#if def}
                                            <td
                                                rowspan={totalRowsPerProduct}
                                                class="sticky-col col-custom master-cell"
                                                class:bold={colKey === 'itemGroup' || colKey === 'productType'}
                                                class:font-mono={colKey === 'itemCode'}
                                                style="left: {getColLeft($filteredSupplyPlan.visibleCols, colKey, colWidths)}; width: {colWidths[colKey] || 80}px; min-width: {colWidths[colKey] || 80}px; max-width: {colWidths[colKey] || 80}px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;"
                                                title={colKey === 'itemCode' && product.itemSpec ? product.itemSpec : (product[colKey] || '')}
                                            >
                                                {product[colKey] || "-"}
                                            </td>
                                        {/if}
                                    {/each}

                                    <td
                                        rowspan={rowDefs.length}
                                        class="sticky-col col-planweek planweek-cell font-mono"
                                        style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                    >
                                        {$filteredSupplyPlan.baseWeek}
                                    </td>
                                {/if}

                                <!-- 항목명 및 기획안 툴팁 -->
                                <td
                                    class="sticky-col col-metric metric-cell"
                                    class:metric-shortage={metric.key === 'shortage'}
                                    style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                                    title="{metric.label} ({metric.badge}): {metric.summary}"
                                >
                                    {metric.label}
                                </td>

                                <!-- 주차별 데이터 & 월소계 -->
                                {#each $filteredSupplyPlan.activeMonths as m}
                                    {#each (monthSummaryMode ? [] : m.weeks) as w}
                                        <td
                                            class="cell-num"
                                            class:actual-cell={isActualWeek(w)}
                                            class:order-link={metric.key === "order" && getWeekVal(baseMetrics, w, metric.key) !== 0}
                                            class:order-selected={metric.key === "order" && isDetailOpen && detailCellKey === orderCellKey(product, $filteredSupplyPlan.baseWeek, "W", w)}
                                            title={metric.key === "order" && getWeekVal(baseMetrics, w, metric.key) !== 0 ? "더블클릭: 수주 상세" : undefined}
                                            on:click={() => metric.key === "order" && handleOrderCellTap(product, $filteredSupplyPlan.baseWeek, "W", w, getWeekVal(baseMetrics, w, metric.key))}
                                        >
                                            {formatNumber(getWeekVal(baseMetrics, w, metric.key))}
                                        </td>
                                    {/each}
                                    <!-- 월소계 셀 -->
                                    <td
                                        class="cell-num month-subtotal-cell"
                                        class:order-link={metric.key === "order" && getMonthVal(baseMetrics, m, metric.key) !== 0}
                                        class:order-selected={metric.key === "order" && isDetailOpen && detailCellKey === orderCellKey(product, $filteredSupplyPlan.baseWeek, "M", m)}
                                        title={metric.key === "order" && getMonthVal(baseMetrics, m, metric.key) !== 0 ? "더블클릭: 수주 상세" : undefined}
                                        on:click={() => metric.key === "order" && handleOrderCellTap(product, $filteredSupplyPlan.baseWeek, "M", m, getMonthVal(baseMetrics, m, metric.key))}
                                    >
                                        {formatNumber(getMonthVal(baseMetrics, m, metric.key))}
                                    </td>
                                {/each}

                                <!-- 총계(Total) 셀 -->
                                <td class="cell-num grand-total-cell">
                                    {formatNumber(getTotalVal(baseMetrics, metric.key))}
                                </td>
                            </tr>
                        {/each}

                        <!-- 2. 비교 주차 (복수주차일 때만 6행) -->
                        {#if $filteredSupplyPlan.isMultiWeek}
                            {#each rowDefs as metric, mIdx}
                                <tr class="data-row compare-row">
                                    {#if mIdx === 0}
                                        <td
                                            rowspan={rowDefs.length}
                                            class="sticky-col col-planweek planweek-cell compare-week-tag font-mono"
                                            style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                        >
                                            {$filteredSupplyPlan.compareWeek}
                                        </td>
                                    {/if}

                                    <td
                                        class="sticky-col col-metric metric-cell"
                                        class:metric-shortage={metric.key === 'shortage'}
                                        style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                                        title="{metric.label}: {metric.summary}"
                                    >
                                        {metric.label}
                                    </td>

                                    {#each $filteredSupplyPlan.activeMonths as m}
                                        {#each (monthSummaryMode ? [] : m.weeks) as w}
                                            <td
                                                class="cell-num"
                                                class:actual-cell={isActualWeek(w)}
                                                class:order-link={metric.key === "order" && getWeekVal(compareMetrics, w, metric.key) !== 0}
                                                class:order-selected={metric.key === "order" && isDetailOpen && detailCellKey === orderCellKey(product, $filteredSupplyPlan.compareWeek, "W", w)}
                                                title={metric.key === "order" && getWeekVal(compareMetrics, w, metric.key) !== 0 ? "더블클릭: 수주 상세" : undefined}
                                                on:click={() => metric.key === "order" && handleOrderCellTap(product, $filteredSupplyPlan.compareWeek, "W", w, getWeekVal(compareMetrics, w, metric.key))}
                                            >
                                                {formatNumber(getWeekVal(compareMetrics, w, metric.key))}
                                            </td>
                                        {/each}
                                        <td
                                            class="cell-num month-subtotal-cell"
                                            class:order-link={metric.key === "order" && getMonthVal(compareMetrics, m, metric.key) !== 0}
                                            class:order-selected={metric.key === "order" && isDetailOpen && detailCellKey === orderCellKey(product, $filteredSupplyPlan.compareWeek, "M", m)}
                                            title={metric.key === "order" && getMonthVal(compareMetrics, m, metric.key) !== 0 ? "더블클릭: 수주 상세" : undefined}
                                            on:click={() => metric.key === "order" && handleOrderCellTap(product, $filteredSupplyPlan.compareWeek, "M", m, getMonthVal(compareMetrics, m, metric.key))}
                                        >
                                            {formatNumber(getMonthVal(compareMetrics, m, metric.key))}
                                        </td>
                                    {/each}

                                    <td class="cell-num grand-total-cell">
                                        {formatNumber(getTotalVal(compareMetrics, metric.key))}
                                    </td>
                                </tr>
                            {/each}

                            <!-- 3. 차이 (Diff) (복수주차일 때만 6행) -->
                            {#each rowDefs as metric, mIdx}
                                <tr class="data-row diff-row" class:product-last-row={mIdx === rowDefs.length - 1}>
                                    {#if mIdx === 0}
                                        <td
                                            rowspan={rowDefs.length}
                                            class="sticky-col col-planweek planweek-cell diff-tag font-mono bold"
                                            style="left: {getPlanWeekLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 85px; min-width: 85px;"
                                        >
                                            차이 (Diff)
                                        </td>
                                    {/if}

                                    <td
                                        class="sticky-col col-metric metric-cell diff-metric"
                                        style="left: {getMetricLeft($filteredSupplyPlan.visibleCols, colWidths)}; width: 60px; min-width: 60px;"
                                    >
                                        {metric.label}
                                    </td>

                                    {#each $filteredSupplyPlan.activeMonths as m}
                                        {#each (monthSummaryMode ? [] : m.weeks) as w}
                                            <td
                                                class="cell-num diff-cell"
                                                class:diff-pos={getWeekVal(diffMetrics, w, metric.key) > 0}
                                                class:diff-neg={getWeekVal(diffMetrics, w, metric.key) < 0}
                                                class:actual-cell={isActualWeek(w)}
                                            >
                                                {formatDiff(getWeekVal(diffMetrics, w, metric.key))}
                                            </td>
                                        {/each}
                                        <td
                                            class="cell-num month-subtotal-cell diff-cell"
                                            class:diff-pos={getMonthVal(diffMetrics, m, metric.key) > 0}
                                            class:diff-neg={getMonthVal(diffMetrics, m, metric.key) < 0}
                                        >
                                            {formatDiff(getMonthVal(diffMetrics, m, metric.key))}
                                        </td>
                                    {/each}

                                    <td
                                        class="cell-num grand-total-cell diff-cell"
                                        class:diff-pos={getTotalVal(diffMetrics, metric.key) > 0}
                                        class:diff-neg={getTotalVal(diffMetrics, metric.key) < 0}
                                    >
                                        {formatDiff(getTotalVal(diffMetrics, metric.key))}
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                    {/each}

                    <!-- 무한 스크롤 추가 로딩 안내 및 전체 펼치기 -->
                    {#if hasMore}
                        <tr class="infinite-loading-row">
                            <td colspan="100" class="infinite-loading-cell">
                                <div class="infinite-loading-content">
                                    <span class="mini-spinner"></span>
                                    <span class="infinite-text">스크롤을 아래로 내리면 추가 품목이 자동으로 로딩됩니다. ({displayedItems.length} / {$filteredSupplyPlan.items.length}개 표시 중)</span>
                                    <button type="button" class="btn-load-all" on:click={loadAll}>⚡ 전체 한 번에 펼치기 ({$filteredSupplyPlan.items.length}개)</button>
                                </div>
                            </td>
                        </tr>
                    {/if}
                {/if}
            </tbody>
        </table>
    </div>
</div>

<!-- 수주 상세 팝업 (수주 행 수량 더블클릭) -->
<OrderDetailModal
    isOpen={isDetailOpen}
    params={detailParams}
    meta={detailMeta}
    on:close={() => (isDetailOpen = false)}
/>

<style>
    .list-container {
        background: #ffffff;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex: 1;
        min-height: 0;
        height: 100%;
    }

    .list-header {
        padding: 10px 16px;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
    }
    .title-area {
        display: flex;
        align-items: center;
        width: 100%;
        justify-content: space-between;
    }
    .title-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .title-area h3 {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 800;
        color: #1e293b;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .badge-count {
        font-size: 0.72rem;
        background: #e2e8f0;
        color: #475569;
        padding: 2px 8px;
        border-radius: 12px;
        font-weight: 700;
    }

    .header-right-group {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    .header-action-group {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        font-size: 0.74rem;
        font-weight: 700;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;
        line-height: 1.2;
    }
    .toggle-summary-btn {
        background: #f8fafc;
        border: 1px solid #cbd5e1;
        color: #334155;
    }
    .toggle-summary-btn:hover {
        background: #f1f5f9;
        border-color: #94a3b8;
        color: #0f172a;
    }
    .toggle-summary-btn.is-active {
        background: #eff6ff;
        border-color: #93c5fd;
        color: #1d4ed8;
    }

    /* 테이블 스크롤 영역 */
    .table-scroll-wrapper {
        flex: 1;
        min-height: 0;
        overflow: auto;
        position: relative;
    }
    .table-scroll-wrapper::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    .table-scroll-wrapper::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
    }
    .table-scroll-wrapper::-webkit-scrollbar-track {
        background: #f8fafc;
    }

    /* Ctrl 키 활성화 시 잡기(손바닥) 커서 및 패닝 스크롤 스타일 */
    .table-scroll-wrapper.pan-ready,
    .table-scroll-wrapper.pan-ready * {
        cursor: grab !important;
        user-select: none !important;
    }

    .table-scroll-wrapper.is-panning,
    .table-scroll-wrapper.is-panning * {
        cursor: grabbing !important;
        user-select: none !important;
    }

    .psi-table {
        table-layout: fixed;
        width: max-content;
        min-width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 0.82rem;
        text-align: center;
        color: #1e293b;
    }

    /* 헤더 스타일 (단일 행 헤더 + 상단 전체합계 sticky 고정) */
    .psi-table thead {
        position: sticky;
        top: 0;
        z-index: 25;
        background: #f1f5f9;
    }
    .psi-table th {
        background: #f1f5f9;
        color: #334155;
        font-weight: 700;
        padding: 5px 6px;
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #cbd5e1;
        border-top: 1px solid #cbd5e1;
        white-space: nowrap;
        font-size: 0.78rem;
        background-clip: padding-box;
        height: 34px;
        box-sizing: border-box;
    }
    .psi-table th:first-child {
        border-left: 1px solid #cbd5e1;
    }

    .psi-header-row {
        height: 34px;
    }
    .psi-table thead .sticky-col {
        z-index: 45;
        background: #f1f5f9;
    }

    .week-th {
        width: 44px;
        min-width: 44px;
        max-width: 44px;
        padding: 4px 1px !important;
        background: #ffffff !important;
        font-variant-numeric: tabular-nums;
        font-size: 0.73rem;
    }
    .week-th span {
        letter-spacing: -0.3px;
        display: inline-block;
    }
    .actual-th {
        background: #f8fafc !important;
    }
    .month-subtotal-th {
        background: #fef3c7 !important;
        color: #92400e;
        font-weight: 800;
        width: 70px;
        min-width: 70px;
        max-width: 70px;
        padding: 4px 3px !important;
        font-size: 0.74rem;
        letter-spacing: -0.3px;
        border-left: 2px solid #fde68a !important;
        border-right: 2px solid #fde68a !important;
        white-space: nowrap;
    }
    .total-th {
        background: #e0e7ff !important;
        color: #3730a3;
        font-weight: 800;
        width: 68px;
        min-width: 68px;
        max-width: 68px;
        padding: 4px 3px !important;
        font-size: 0.75rem;
        letter-spacing: -0.3px;
        border-left: 2px solid #c7d2fe !important;
        white-space: nowrap;
    }

    /* Sticky 좌측 고정 컬럼 */
    .sticky-col {
        position: sticky !important;
        background: #ffffff;
        z-index: 10;
        border: 1px solid #cbd5e1;
        background-clip: padding-box;
        box-sizing: border-box;
    }

    th.col-custom {
        position: sticky !important;
        user-select: none;
        overflow: visible;
        padding-left: 6px !important;
        padding-right: 6px !important;
    }

    td.col-custom {
        position: sticky !important;
        user-select: text;
        padding-left: 6px !important;
        padding-right: 6px !important;
    }

    .col-header-text {
        display: block;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
    }
    /* 정렬 가능한 헤더: 클릭 가능 표시 + 활성 정렬 컬럼 강조 */
    .sortable-th {
        cursor: pointer;
    }
    .sortable-th:hover {
        background: #e2e8f0 !important;
    }
    .sort-icon {
        margin-left: 3px;
        font-size: 0.62rem;
        color: #94a3b8;
    }
    .sortable-th:hover .sort-icon {
        color: #475569;
    }
    .sortable-th.sort-active .sort-icon {
        color: #2563eb;
    }
    .col-resize-handle {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 6px;
        cursor: col-resize;
        z-index: 55;
        background: transparent;
        transition: background 0.15s ease;
    }
    .col-resize-handle:hover,
    .col-resize-handle.is-active {
        background: #3b82f6 !important;
    }

    .col-metric {
        box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
    }

    /* 데이터 셀 스타일 */
    .psi-table td {
        padding: 4px 6px;
        border: 1px solid #e2e8f0;
        white-space: nowrap;
        height: 25px;
        box-sizing: border-box;
    }
    .cell-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        padding: 3px 4px !important;
        color: #1e293b;
        font-size: 0.76rem;
        width: 44px;
        min-width: 44px;
        max-width: 44px;
        box-sizing: border-box;
    }
    .month-subtotal-cell {
        width: 70px;
        min-width: 70px;
        max-width: 70px;
        padding: 3px 4px !important;
        box-sizing: border-box;
    }
    .grand-total-cell {
        width: 68px;
        min-width: 68px;
        max-width: 68px;
        padding: 3px 4px !important;
        box-sizing: border-box;
    }

    .master-cell {
        background: #f8fafc;
        vertical-align: middle;
        font-size: 0.78rem;
    }
    .planweek-cell {
        vertical-align: middle;
        background: #f8fafc;
        font-size: 0.78rem;
        color: #334155;
    }
    .compare-week-tag {
        background: #fdf4ff;
        color: #86198f;
    }
    .diff-tag {
        background: #f0fdf4;
        color: #166534;
    }
    .metric-cell {
        background: #ffffff;
        font-weight: 700;
        color: #475569;
        font-size: 0.78rem;
        cursor: help;
    }
    .metric-shortage {
        color: #dc2626 !important;
        font-weight: 800;
    }

    .actual-cell {
        background: #f8fafc;
    }

    /* 수주 행의 0이 아닌 수량: 더블클릭 시 수주 상세 팝업 */
    .order-link {
        cursor: pointer;
        /* 터치 기기에서 더블탭이 확대로 처리되지 않도록 */
        touch-action: manipulation;
    }
    .order-link:hover {
        background: #dbeafe !important;
        color: #1d4ed8;
        text-decoration: underline;
        text-decoration-style: dotted;
        text-underline-offset: 2px;
    }
    /* 팝업에 표시 중인 셀: 불 켜진 것처럼 강조 (은은하게 깜빡이는 글로우) */
    .order-selected,
    .order-selected:hover {
        position: relative;
        z-index: 6;
        background: #fef08a !important;
        color: #1e3a8a !important;
        font-weight: 800;
        text-decoration: none;
        box-shadow: inset 0 0 0 2px #2563eb, 0 0 10px 2px rgba(37, 99, 235, 0.55);
        animation: order-selected-glow 1.6s ease-in-out infinite;
    }
    @keyframes order-selected-glow {
        0%, 100% { box-shadow: inset 0 0 0 2px #2563eb, 0 0 6px 1px rgba(37, 99, 235, 0.35); }
        50% { box-shadow: inset 0 0 0 2px #2563eb, 0 0 14px 4px rgba(37, 99, 235, 0.7); }
    }

    .month-subtotal-cell {
        background: #fffbeb;
        font-weight: 700;
        color: #92400e;
        border-left: 2px solid #fde68a;
        border-right: 2px solid #fde68a;
    }
    .grand-total-cell {
        background: #eef2ff;
        font-weight: 800;
        color: #3730a3;
        border-left: 2px solid #c7d2fe;
    }

    .diff-row {
        background: #f0fdf4;
    }
    .diff-row .sticky-col {
        background: #f0fdf4;
    }
    .diff-metric {
        color: #166534;
    }
    .diff-pos {
        color: #16a34a;
        font-weight: 700;
    }
    .diff-neg {
        color: #1e293b;
        font-weight: 600;
    }

    .product-first-row td {
        border-top: 2px solid #94a3b8;
    }
    .product-last-row td {
        border-bottom: 2px solid #94a3b8;
    }

    /* 상단 전체 합계 행 애니메이션 (스르륵 펼쳐지고 접히는 매끄러운 트랜지션) */
    .grand-summary-row {
        background: #f8fafc;
    }
    .grand-summary-row td {
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #cbd5e1;
        background: #f8fafc;
        padding: 0 4px !important;
        height: auto !important;
        overflow: hidden;
        white-space: nowrap;
        box-sizing: border-box;
        transition: border-color 0.28s ease;
    }
    .grand-summary-row td:first-child {
        border-left: 1px solid #cbd5e1;
    }

    .summary-cell-wrap {
        height: 25px;
        line-height: 25px;
        opacity: 1;
        overflow: hidden;
        white-space: nowrap;
        box-sizing: border-box;
        transition: height 0.22s cubic-bezier(0.2, 0.9, 0.3, 1),
                    opacity 0.18s ease-out;
    }
    .summary-cell-wrap.text-right {
        text-align: right;
    }
    .summary-total-badge {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .summary-planweek-wrap {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* 닫힐 때: 모든 내부 래퍼 높이를 0으로 수렴 */
    .summary-cell-wrap:not(.is-open) {
        height: 0px !important;
        opacity: 0 !important;
        pointer-events: none;
    }

    /* 닫혔을 때 테두리 및 여백 완전 제거 (0px 완벽 밀착) */
    .grand-summary-row.summary-collapsed td {
        border-color: transparent !important;
        border-width: 0 !important;
        border: none !important;
        height: 0px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        box-shadow: none !important;
    }

    .summary-top-mode {
        background: #f8fafc;
    }
    .summary-top-mode:not(.summary-collapsed):last-of-type td,
    .summary-top-mode:not(.summary-collapsed).product-last-row td {
        border-bottom: 2px solid #64748b !important;
        box-shadow: 0 4px 6px -2px rgba(0, 0, 0, 0.1);
    }
    .grand-summary-row .sticky-col {
        z-index: 40;
    }
    .col-total-label {
        background: #e2e8f0;
        vertical-align: middle;
        font-size: 0.85rem;
        color: #0f172a;
    }

    .bold { font-weight: 700; }
    .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
    .text-primary { color: #2563eb; }

    .empty-state {
        padding: 40px;
        text-align: center;
        background: #ffffff;
    }
    .empty-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #94a3b8;
    }
    .empty-content p {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 600;
    }

    /* 로딩 인디케이터 오버레이 (조회 중 전체 페이지 표시) */
    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.88);
        backdrop-filter: blur(3px);
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .loading-modal {
        background: #ffffff;
        border: 1.5px solid #cbd5e1;
        border-radius: 8px;
        padding: 24px 32px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        max-width: 420px;
        text-align: center;
    }
    .spinner {
        width: 36px;
        height: 36px;
        border: 3.5px solid #e2e8f0;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    .loading-text strong {
        display: block;
        font-size: 0.95rem;
        color: #1e293b;
        margin-bottom: 6px;
    }
    .loading-text span {
        font-size: 0.8rem;
        color: #64748b;
        line-height: 1.4;
    }

    /* 무한 스크롤 안내 및 펼치기 버튼 */
    .infinite-loading-row {
        background: #f8fafc;
    }
    .infinite-loading-cell {
        padding: 16px 20px;
        text-align: center;
        border-bottom: 2px dashed #cbd5e1;
        background: #f8fafc;
    }
    .infinite-loading-content {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: #ffffff;
        padding: 8px 18px;
        border-radius: 20px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }
    .mini-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid #cbd5e1;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        display: inline-block;
    }
    .infinite-text {
        font-size: 0.8rem;
        color: #475569;
        font-weight: 600;
    }
    .btn-load-all {
        padding: 4px 10px;
        font-size: 0.75rem;
        font-weight: 700;
        color: #2563eb;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .btn-load-all:hover {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1d4ed8;
    }

    /* 월 합계 모드: 주차 컬럼은 숨기고, 월/년 합계(노란색 칸)와 계(Total)만 표시 */
    .psi-table.month-sum-mode .month-subtotal-th,
    .psi-table.month-sum-mode .month-subtotal-cell {
        width: 110px;
        min-width: 110px;
        max-width: 110px;
        font-size: 0.82rem;
    }
</style>
